import { Inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, takeUntil } from 'rxjs/operators';

import { AccountSelectors } from '@core/account-data';
import { CurrentUserPreferencesFacade } from '@core/account-logic';
import { filterBoolean, DocumentExternalScriptsLoader } from '@core/common-legacy';
import { DevFlag, injectDevFlag } from '@core/dev-flags-data';

import {
  STRIPE_OPTIONS,
  STRIPE_PUBLISHABLE_KEY_BV,
  STRIPE_PUBLISHABLE_KEY_GMBH,
  STRIPE_PUBLISHABLE_KEY_INC,
  STRIPE_PUBLISHABLE_KEY_SAS,
} from './stripe-token';
import type {
  Element,
  ElementOptions,
  Elements,
  Status,
  TokenResult,
} from '../../types/stripe.interfaces';

const incCountires = ['US', 'CA', 'UM', 'PR', 'VI', 'GU', 'MP', 'AS'];
const gmbhCountries = ['DE', 'AT', 'CH'];
const sasCountries = ['FR'];

@Injectable({ providedIn: 'root' })
export class StripeService {
  public elements$: BehaviorSubject<{
    cardNumber: Element;
    cardExpiry: Element;
    cardCvc: Element;
  } | null> = new BehaviorSubject(null);

  private stripe: any;
  private colors: any;
  private ngOnDestroy$ = new Subject<void>();
  private tellentSasEnabled = injectDevFlag(DevFlag.TellentSas);

  constructor(
    @Inject(STRIPE_PUBLISHABLE_KEY_BV) private key: string,
    @Inject(STRIPE_PUBLISHABLE_KEY_GMBH) private keyGmbh: string,
    @Inject(STRIPE_PUBLISHABLE_KEY_INC) private keyInc: string,
    @Inject(STRIPE_PUBLISHABLE_KEY_SAS) private keySas: string,
    @Inject(STRIPE_OPTIONS) private options: string,
    private webappExternalScriptsLoader: DocumentExternalScriptsLoader,
    private store: Store,
    private currentUserPreferencesFacade: CurrentUserPreferencesFacade,
  ) {
    const stripeBundle = this.webappExternalScriptsLoader.create({
      name: 'stripe',
      url: '//js.stripe.com/v3/',
    });
    combineLatest([
      this.store.select(AccountSelectors.currentCompany).pipe(
        filterBoolean,
        distinctUntilChanged(
          (x, y) => x.billingInformation?.countryCode === y.billingInformation?.countryCode,
        ),
      ),
      stripeBundle.load().pipe(
        filter((status: Status) => status.loaded),
        map(_ => (<any>window).Stripe),
        first(),
      ),
    ]).subscribe(([currentCompany, stripe]) => {
      const countryCode = currentCompany.billingInformation?.countryCode || '';
      const chargebeeEntity = currentCompany.chargebeeEntity;

      const stripeKey = this.getStripeKey(countryCode, chargebeeEntity);

      this.stripe = stripe(stripeKey, this.options);
      this.elements$.next(this.createElements());
    });
    this.currentUserPreferencesFacade.activeTheme$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(theme => {
        this.colors = theme;
      });
  }

  public handleCardPayment(paymentIntentSecret: string): Promise<any> {
    return this.stripe.handleCardPayment(paymentIntentSecret);
  }
  public handleCardAction(paymentIntentSecret: string): Promise<any> {
    return this.stripe.handleCardAction(paymentIntentSecret);
  }

  public createToken(cardNumberElement: Element, tokenData: any): Promise<TokenResult> {
    return this.stripe.createToken(cardNumberElement, tokenData);
  }
  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private createElements(): {
    cardNumber: Element;
    cardExpiry: Element;
    cardCvc: Element;
  } {
    const elements = <Elements>this.stripe.elements(this.options);

    const cardNumber = elements.create('cardNumber', this.getStyles());
    const cardExpiry = elements.create('cardExpiry', this.getStyles());
    const cardCvc = elements.create('cardCvc', this.getStyles());

    return {
      cardNumber,
      cardExpiry,
      cardCvc,
    };
  }

  private getStripeKey(countryCode: string, chargebeeEntity: string | null): string {
    if (chargebeeEntity === null) {
      return this.getStripeKeyFromCountryCode(countryCode);
    } else {
      return this.getStripeKeyFromChargebeeEntity(chargebeeEntity);
    }
  }

  private getStripeKeyFromCountryCode(countryCode: string): string {
    if (incCountires.includes(countryCode)) {
      return this.keyInc;
    } else if (gmbhCountries.includes(countryCode)) {
      return this.keyGmbh;
    } else if (this.tellentSasEnabled && sasCountries.includes(countryCode)) {
      return this.keySas;
    } else {
      return this.key;
    }
  }

  private getStripeKeyFromChargebeeEntity(chargebeeEntity: string): string {
    if (chargebeeEntity === 'inc') {
      return this.keyInc;
    } else if (chargebeeEntity === 'gmbh') {
      return this.keyGmbh;
    } else if (this.tellentSasEnabled && chargebeeEntity === 'sas') {
      return this.keySas;
    } else {
      return this.key;
    }
  }

  private getStyles(): ElementOptions {
    return {
      style: {
        base: {
          iconColor: this.colors.colorIconDefault,
          color: this.colors.colorTextStrongDefault,
          fontWeight: 400,
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSize: '14px',

          '::placeholder': {
            color: this.colors.colorTextLowDefault,
            fontWeight: 300,
          },
        },
        invalid: {
          color: this.colors.colorTextStrongDefault,
        },
      },
      classes: {
        focus: 'is-focused',
        invalid: 'is-invalid',
      },
    };
  }
}
